import React from "react";
import { Redirect, useLocation } from "react-router";
import { useRecoilValue } from "recoil";
import { languageState } from "state/language";
import { getCurrentMultisiteBase } from "util/MagnoliaAppHelpers";
import {
  detectBrowserLanguage,
  getMultisiteDefaultLanguage,
  getMultisiteLanguages,
  isSingleTreeMultisite,
} from "util/multisiteLanguages";

/**
 * Handles language redirect for the root path (/).
 * In production: used when users access the domain root (e.g., www.albis.com/).
 * In development: used when users access /{multisite} (e.g., localhost:41812/albis-distribution).
 * 
 * For single-tree multisites (like albis-distribution):
 * - Detects browser language and redirects to best matching language
 * - Falls back to multisite-specific default language (e.g., English for albis-distribution)
 * 
 * For multi-tree multisites:
 * - Uses the language from state (existing behavior)
 * 
 * Note: Skips redirect in Magnolia preview/editor mode to allow editing root pages.
 */
const LanguageRedirect = () => {
  const language = useRecoilValue(languageState);
  const location = useLocation();
  const multisiteBase = getCurrentMultisiteBase();

  // Check if this multisite uses single-tree structure
  if (isSingleTreeMultisite(multisiteBase)) {
    const availableLanguages = getMultisiteLanguages(multisiteBase);
    const defaultLanguage = getMultisiteDefaultLanguage(multisiteBase);
    
    // In Magnolia preview mode, use default language to allow editing the root page
    const detectedLanguage = location.search.includes("mgnlPreview=")
      ? defaultLanguage
      : detectBrowserLanguage(availableLanguages, defaultLanguage);

    // Build redirect URL based on environment
    let redirectPath: string;
    if (process.env.NODE_ENV === "production") {
      // Production: www.albis.com/ → www.albis.com/en/
      redirectPath = `/${detectedLanguage}/`;
    } else {
      // Development: localhost:41812/albis-distribution → localhost:41812/albis-distribution/en/
      redirectPath = `/${multisiteBase}/${detectedLanguage}/`;
    }
    
    // Preserve query params and hash (important for Magnolia preview mode)
    if (location.search) {
      redirectPath += location.search;
    }
    if (location.hash) {
      redirectPath += location.hash;
    }
    
    return <Redirect to={redirectPath} />;
  }

  // For multi-tree multisites, use existing behavior
  return <Redirect to={`/${language}/`} />;
};

export default LanguageRedirect;
